import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Divider,
  List,
  ListItem,
  ListIcon,
  Badge,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaIndustry,
  FaPlane,
  FaCar,
  FaFlask,
  FaDollarSign,
  FaCube,
  FaClock,
  FaMicrochip,
  FaCog,
  FaChartLine,
  FaBolt,
  FaWind,
  FaFire,
} from 'react-icons/fa';
import Footer from '../Footer';

const TaylorGreenVortexTurbulence = () => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.800');
  const [showAnimation, setShowAnimation] = useState(false);

  return (
    <Box minH="100vh" bg="gray.50">
      <Helmet>
        <title>3D Taylor–Green Vortex: High-Fidelity Turbulence Simulation | SRS Blog</title>
        <meta name="description" content="3D Taylor–Green vortex benchmark: laminar-to-turbulent transition and the energy cascade captured with GPU-accelerated high-order CFD in the cloud." />
        <link rel="canonical" href="https://srs.numericalai.net/blog/taylor-green-vortex-turbulence" />
        <meta property="og:title" content="3D Taylor–Green Vortex: High-Fidelity Turbulence Simulation | SRS Blog" />
        <meta property="og:description" content="3D Taylor–Green vortex benchmark: laminar-to-turbulent transition and the energy cascade captured with GPU-accelerated high-order CFD in the cloud." />
        <meta property="og:url" content="https://srs.numericalai.net/blog/taylor-green-vortex-turbulence" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="3D Taylor–Green Vortex: High-Fidelity Turbulence Simulation | SRS Blog" />
        <meta name="twitter:description" content="3D Taylor–Green vortex benchmark: laminar-to-turbulent transition and the energy cascade captured with GPU-accelerated high-order CFD in the cloud." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image 
                src="/simple-logo.png" 
                alt="SRS Logo" 
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <Button
              leftIcon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </HStack>
        </Container>
      </Box>

      <Container maxW="5xl" py={12}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center">
            <Badge colorScheme="purple" fontSize="md" mb={4} px={3} py={1}>
              Turbulence Benchmark
            </Badge>
            <Heading 
              as="h1"
              size="2xl" 
              color="brand.500" 
              mb={6}
              fontWeight="bold"
            >
              3D Taylor–Green Vortex Simulation
            </Heading>
            <Heading 
              size="lg" 
              color="gray.600" 
              mb={6}
              fontWeight="medium"
            >
              High-Fidelity 3D Turbulence Simulation for Industrial Flow Physics
            </Heading>
            
            <Text 
              fontSize="xl" 
              color="gray.700" 
              lineHeight="tall"
              mb={6}
              textAlign="left"
            >
              Experience next-generation computational fluid dynamics (CFD) with NumericalAI, a high-performance simulation environment designed for industry-scale flow modeling and advanced turbulence research.
            </Text>
            
            <Text 
              fontSize="lg" 
              color="gray.600" 
              lineHeight="tall"
              textAlign="left"
            >
              The example below demonstrates the <strong>3D Taylor–Green Vortex</strong>, a canonical benchmark widely used to validate high-order solvers, viscous dissipation modeling, and turbulence transition in complex flow regimes. NumericalAI's simulation engine resolves fine-scale vortical structures with exceptional accuracy using MFC's high-order WENO schemes and fully 3D compressible Navier–Stokes physics.
            </Text>
          </Box>

          {/* Animation Toggle */}
          <Box textAlign="center">
            <Button
              size="lg"
              colorScheme={showAnimation ? "gray" : "brand"}
              onClick={() => setShowAnimation(!showAnimation)}
              leftIcon={<FaWind />}
            >
              {showAnimation ? "Show Snapshots" : "Show Full Animation"}
            </Button>
          </Box>

          {/* Images or Animation */}
          {showAnimation ? (
            <Box 
              bg={cardBg}
              borderRadius="xl"
              overflow="hidden"
              shadow="2xl"
              border="1px"
              borderColor={borderColor}
            >
              <Image 
                src={require('../../img/blogs/3D_TaylerGreenVortex_Vel-Norm_Contour_Animation_Simulation_NumericalAI_Image.gif')}
                alt="Taylor-Green Vortex Animation"
                w="100%"
                objectFit="contain"
                bg="gray.100"
              />
              <Box p={6} bg={sectionBg}>
                <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2} textAlign="center">
                  <strong>Full Time Evolution — Velocity Magnitude</strong>
                </Text>
                <Text fontSize="md" color="gray.600" lineHeight="tall" textAlign="center">
                  Complete temporal evolution showing the transition from organized laminar vortices to fully developed turbulence. The animation captures the entire energy cascade process from large-scale coherent structures down to dissipation scales.
                </Text>
              </Box>
            </Box>
          ) : (
            <Tabs variant="enclosed" colorScheme="brand">
              <TabList bg={sectionBg} borderRadius="xl" p={2} flexWrap="wrap">
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaWind} mr={2} /> Q-Criterion t=0.17s
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaWind} mr={2} /> Q-Criterion t=0.29s
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaWind} mr={2} /> Q-Criterion t=0.48s
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaBolt} mr={2} /> Velocity t=0.17s
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaBolt} mr={2} /> Velocity t=0.29s
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaBolt} mr={2} /> Velocity t=0.48s
                </Tab>
              </TabList>

              <TabPanels>
                {/* Q-Criterion Panels */}
                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/3D_TaylerGreenVortex_Q-Criterion_IsoSurface_t0-17s_Simulation_NumericalAI_Image.jpg')}
                      alt="Q-Criterion Isosurface t=0.17s"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Early Transition (t = 0.17 t₀)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Q-criterion isosurface capturing the formation of coherent vortices during early transition. The flow retains most of its initial symmetry with well-organized large-scale vortical structures. The Q-criterion (Q = ½(||Ω||² - ||S||²)) identifies regions where rotation dominates over strain, making it ideal for vortex visualization.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/3D_TaylerGreenVortex_Q-Criterion_IsoSurface_t0-29s_Simulation_NumericalAI_Image.jpg')}
                      alt="Q-Criterion Isosurface t=0.29s"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Vortex Breakdown (t = 0.29 t₀)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Intermediate-stage vortex breakdown and energy cascade into smaller turbulent structures. The organized vortex tubes begin fragmenting into complex three-dimensional structures. This phase represents the critical transition where energy transfers from large scales to progressively smaller eddies — the essence of the turbulent energy cascade.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/3D_TaylerGreenVortex_Q-Criterion_IsoSurface_t0-48s_Simulation_NumericalAI_Image.jpg')}
                      alt="Q-Criterion Isosurface t=0.48s"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Fully Developed Turbulence (t = 0.48 t₀)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Fully developed turbulence resolved through high-order numerical dissipation and viscosity modeling. The flow exhibits a rich spectrum of scales with small-scale vortices filling the domain. The high-order WENO5 scheme maintains sharp resolution of these structures without excessive numerical dissipation that would artificially damp the turbulence.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                {/* Velocity Magnitude Panels */}
                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/3D_TaylerGreenVortex_Vel-Norm_Contour_t0-17s_Simulation_NumericalAI_Image.jpg')}
                      alt="Velocity Magnitude t=0.17s"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Velocity Field — Early Stage (t = 0.17 t₀)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Velocity-magnitude contours illustrating the symmetry and structure of early vortex dynamics. The flow field shows clear periodic patterns with distinct high and low velocity regions corresponding to the vortex cores and saddle points of the Taylor-Green initial condition.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/3D_TaylerGreenVortex_Vel-Norm_Contour_t0-29s_Simulation_NumericalAI_Image.jpg')}
                      alt="Velocity Magnitude t=0.29s"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Velocity Field — Transition (t = 0.29 t₀)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Progressive deformation of flow features during vortex stretching and dissipation. The velocity field becomes increasingly complex as the initial symmetry breaks down. High-velocity regions become more fragmented, indicating energy redistribution across a broader range of scales.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/3D_TaylerGreenVortex_Vel-Norm_Contour_t0-48s_Simulation_NumericalAI_Image.jpg')}
                      alt="Velocity Magnitude t=0.48s"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Velocity Field — Turbulent State (t = 0.48 t₀)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Final turbulent state with rich multi-scale flow structures resolved across the cubic domain. The velocity field exhibits the characteristic chaotic and unpredictable nature of fully developed turbulence, with velocity fluctuations spanning from the domain size down to the grid resolution limit.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}

          <Divider />

          {/* What is Taylor-Green Vortex */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              The Taylor–Green Vortex Benchmark
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Text fontSize="lg" color="gray.700" lineHeight="tall" mb={4}>
                  The <strong>Taylor–Green Vortex</strong> is one of the most important test cases in computational fluid dynamics, originally proposed by G.I. Taylor and A.E. Green in 1937. It serves as a fundamental benchmark for:
                </Text>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>DNS (Direct Numerical Simulation) validation</strong> — Captures all turbulent scales without modeling
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Energy cascade verification</strong> — Tests solver ability to transfer energy from large to small scales
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>High-order scheme assessment</strong> — Sensitive to numerical dissipation and dispersion errors
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Viscous physics accuracy</strong> — Smooth analytical initial conditions with known dissipation rates
                    </Text>
                  </ListItem>
                </List>
              </Box>

              <Box bg="purple.50" p={6} borderRadius="xl" border="2px" borderColor="purple.200">
                <Heading size="md" color="purple.800" mb={3}>
                  Why Re = 1600?
                </Heading>
                <Text fontSize="md" color="purple.900" lineHeight="tall">
                  Reynolds number 1600 is the sweet spot for this benchmark — high enough to exhibit true turbulent transition and energy cascade, yet low enough to be resolved on practical computational grids. At this Re, the flow transitions from laminar to turbulent around t ≈ 4–5 (non-dimensional time), providing clear validation milestones for solver development.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Simulation Highlights */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Simulation Configuration
            </Heading>
            
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={8}>
              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCube} boxSize={6} color="brand.500" />
                    <Heading size="md" color="gray.800">Domain & Resolution</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Formulation:</Text>
                      <Text fontWeight="semibold">3D Cartesian</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Domain Size:</Text>
                      <Text fontWeight="semibold">2πL × 2πL × 2πL</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Grid Resolution:</Text>
                      <Text fontWeight="semibold">256³</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Total Cells:</Text>
                      <Text fontWeight="semibold">16.8 million</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Boundary:</Text>
                      <Text fontWeight="semibold">Periodic (all faces)</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaWind} boxSize={6} color="blue.500" />
                    <Heading size="md" color="gray.800">Flow Parameters</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Reynolds Number:</Text>
                      <Text fontWeight="semibold">Re = 1600</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Mach Number:</Text>
                      <Text fontWeight="semibold">M ≈ 0.08</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Fluid:</Text>
                      <Text fontWeight="semibold">Compressible Gas</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Viscosity Model:</Text>
                      <Text fontWeight="semibold">Constant μ</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">EOS:</Text>
                      <Text fontWeight="semibold">Ideal Gas</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCog} boxSize={6} color="purple.500" />
                    <Heading size="md" color="gray.800">Numerical Methods</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Spatial Scheme:</Text>
                      <Text fontWeight="semibold">WENO5</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Time Integration:</Text>
                      <Text fontWeight="semibold">RK3-TVD</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Riemann Solver:</Text>
                      <Text fontWeight="semibold">HLLC</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">CFL Number:</Text>
                      <Text fontWeight="semibold">0.3</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Mapped WENO:</Text>
                      <Text fontWeight="semibold">Enabled</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaChartLine} boxSize={6} color="orange.500" />
                    <Heading size="md" color="gray.800">Output Fields</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <Text color="gray.700" fontSize="sm">
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      <strong>Q-criterion</strong> (vortex identification)
                    </Text>
                    <Text color="gray.700" fontSize="sm">
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      <strong>Vorticity components</strong> (ωₓ, ωᵧ, ω_z)
                    </Text>
                    <Text color="gray.700" fontSize="sm">
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      <strong>Velocity magnitude</strong>
                    </Text>
                    <Text color="gray.700" fontSize="sm">
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      <strong>Primitive variables</strong> (ρ, u, v, w, p)
                    </Text>
                    <Text color="gray.700" fontSize="sm">
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      <strong>Kinetic energy spectrum</strong>
                    </Text>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>

            <Box bg="blue.50" p={6} borderRadius="xl" border="2px" borderColor="blue.200">
              <HStack spacing={3} mb={3}>
                <Icon as={FaMicrochip} boxSize={6} color="blue.600" />
                <Heading size="md" color="blue.800">Computational Performance</Heading>
              </HStack>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">~54 mins</Text>
                  <Text fontSize="sm" color="gray.600">Wall-Clock Time</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">A100</Text>
                  <Text fontSize="sm" color="gray.600">NVIDIA GPU</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">~1 GB</Text>
                  <Text fontSize="sm" color="gray.600">Peak Memory</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">~17×</Text>
                  <Text fontSize="sm" color="gray.600">Speedup vs CPU</Text>
                </Box>
              </SimpleGrid>
              <Text fontSize="sm" color="gray.600" mt={4} textAlign="center">
                * Based on simulation to t = 10 t₀ with output every 0.5 t₀
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* Key Physics & Results */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Physics Insights & Energy Cascade
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Turbulent Energy Cascade
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={4}>
                  The Taylor–Green Vortex perfectly demonstrates the <strong>Kolmogorov energy cascade</strong>:
                </Text>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Large-Scale Organization (t &lt; 4)</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Initial vortex tubes remain coherent, with kinetic energy concentrated at the integral scale (domain size). Dissipation is minimal as viscous effects are confined to thin boundary layers.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Vortex Stretching & Transition (4 &lt; t &lt; 8)</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Instabilities grow, vortex tubes stretch and fold, creating progressively smaller structures. Energy begins cascading from large to small scales. Peak dissipation rate occurs around t ≈ 9.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Fully Developed Turbulence (t &gt; 8)</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        The flow exhibits a broad spectrum of scales with energy distributed across the inertial range. The energy spectrum follows the -5/3 power law predicted by Kolmogorov theory.
                      </Text>
                    </Box>
                  </ListItem>
                </List>
              </Box>

              <Box bg="green.50" p={6} borderRadius="xl" border="2px" borderColor="green.200">
                <Heading size="md" color="green.800" mb={4}>
                  Validation Metrics
                </Heading>
                <Text fontSize="md" color="green.900" lineHeight="tall" mb={4}>
                  NumericalAI's results match reference DNS data from the literature:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontWeight="semibold" color="green.800" mb={1}>Peak Dissipation Time:</Text>
                    <Text color="gray.700" fontSize="sm">t ≈ 8.9 t₀ (Ref: 9.0 ± 0.1)</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color="green.800" mb={1}>Kinetic Energy Decay:</Text>
                    <Text color="gray.700" fontSize="sm">Matches DNS within 1.5%</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color="green.800" mb={1}>Enstrophy Production:</Text>
                    <Text color="gray.700" fontSize="sm">Peak value within 2% of reference</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color="green.800" mb={1}>Energy Spectrum Slope:</Text>
                    <Text color="gray.700" fontSize="sm">k⁻⁵/³ in inertial range</Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Industrial Applications */}
          <Box>
            <Heading size="lg" color="brand.500" mb={6} textAlign="center">
              Industrial Applications
            </Heading>
            <Text fontSize="md" color="gray.600" mb={6} textAlign="center" maxW="3xl" mx="auto">
              Although the Taylor–Green Vortex is a canonical benchmark, it directly supports industrial CFD workflows requiring reliable turbulence and dissipation modeling:
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaPlane} boxSize={8} color="blue.500" />
                  <Heading size="md" color="gray.800">
                    Aerospace Engineering
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Validation of high-order solvers for transitional flows, wing-tip vortices, and Large Eddy Simulation (LES) preprocessing. Essential for drag reduction and flow control device design where accurate turbulence modeling is critical.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaCar} boxSize={8} color="red.500" />
                  <Heading size="md" color="gray.800">
                    Automotive Industry
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Turbulent mixing in combustion chambers, high-Reynolds-number external aerodynamics, and HVAC system validation. Accurate energy dissipation modeling ensures reliable performance predictions for fuel efficiency and emissions.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaCog} boxSize={8} color="orange.500" />
                  <Heading size="md" color="gray.800">
                    Energy & Turbomachinery
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Rotor–stator flow physics, vortex breakdown in turbines, and loss-generation studies. The Taylor–Green Vortex validates the solver's ability to capture complex 3D unsteady phenomena critical for efficiency optimization.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaFlask} boxSize={8} color="purple.500" />
                  <Heading size="md" color="gray.800">
                    Advanced Manufacturing
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Turbulent mixing in chemical reactors, flow homogenization design, and additive manufacturing process optimization. High-fidelity turbulence resolution ensures proper prediction of mixing efficiency and product quality.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Why NumericalAI */}
          <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
            <Heading size="lg" color="brand.500" mb={6}>
              Why NumericalAI for Industrial CFD?
            </Heading>
            <List spacing={4}>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Robust and scalable HPC performance</strong> — massive 3D turbulence simulations (256³ and beyond) with GPU acceleration
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>High-order accuracy</strong> — WENO5 schemes enable fewer grid points for the same resolution, reducing computational cost
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Validated compressible and multiphase capabilities</strong> — proven on industry-standard benchmarks with published validation data
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Cloud-native workflow</strong> — no installation, no hardware procurement, fully automated industrial workflows
                </Text>
              </ListItem>
            </List>
          </Box>

          {/* Reference */}
          <Box bg="gray.100" p={6} borderRadius="lg" borderLeft="4px" borderColor="brand.500">
            <Heading size="sm" color="gray.700" mb={3}>
              Reference
            </Heading>
            <Text fontSize="sm" color="gray.600" lineHeight="tall">
              Hillewaert, K. (2013). "TestCase C3.5 - DNS of the transition of the Taylor-Green vortex, Re=1600 - Introduction and result summary." <em>2nd International Workshop on High-Order Methods for CFD</em>, Cologne, Germany.
            </Text>
          </Box>

          {/* Business Value */}
          <Box 
            bg="brand.500" 
            color="white" 
            p={8} 
            borderRadius="xl" 
            shadow="xl"
            position="relative"
            overflow="hidden"
          >
            <Box position="relative" zIndex={1}>
              <HStack spacing={3} mb={4}>
                <Icon as={FaDollarSign} boxSize={8} />
                <Heading size="lg">Business Value</Heading>
              </HStack>
              <Text fontSize="lg" lineHeight="tall" mb={4}>
                NumericalAI democratizes world-class turbulence simulation — what used to require specialized HPC clusters and PhD-level expertise is now accessible through an intuitive cloud interface.
              </Text>
              <Text fontSize="lg" lineHeight="tall">
                <strong>ROI Impact:</strong> Validate your turbulence models in hours instead of weeks. Run parametric studies at a fraction of traditional costs. Accelerate product development with confidence in your CFD predictions.
              </Text>
            </Box>
            
            {/* Decorative background */}
            <Box
              position="absolute"
              top="-50%"
              right="-10%"
              width="300px"
              height="300px"
              borderRadius="full"
              bg="whiteAlpha.100"
              zIndex={0}
            />
          </Box>

          {/* Call to Action */}
          <Box textAlign="center" py={8}>
            <VStack spacing={4}>
              <Heading size="md" color="gray.700">
                Ready to Run Your Own Turbulence Simulations?
              </Heading>
              <Text color="gray.600" fontSize="lg" maxW="2xl">
                Experience the power of GPU-accelerated DNS and LES on NumericalAI. Validate your solvers, explore turbulent flows, and accelerate your research.
              </Text>
              <HStack spacing={4} mt={4}>
                <Button
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/start-simulation')}
                >
                  Start Your Simulation
                </Button>
                <Button
                  variant="outline"
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

export default TaylorGreenVortexTurbulence;